import { Formik, FormikHelpers, FormikProps } from "formik";
import _, { cloneDeep } from "lodash";
import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  EventReportInfo,
  ShowsReportInfo,
  SubmitShowsReport,
  SubmitShowsReportRow,
} from "../../../generated/data-contracts";
import {
  AvisUtils,
  ErrorUtils,
  IAxiosError,
  LicenseesUrls,
  LicenseesUtils,
  api,
  prepareCollectionForPost,
  nullsToUndefined,
} from "../../../utils";
import { AvisLoader } from "../../shared/components";
import {
  NotPortalReportMessage,
  SaveAndSubmit,
} from "../../shared/components/form";
import { AvisConfirmModalShort } from "../../shared/components/modal";
import { AvisContext } from "../../shared/context/AvisContext";
import { LicenseReportForm } from "./components/LicenseReportForm";
import { LicenseReportHeader } from "./components/LicenseReportHeader";
import { ReportShowsForm } from "./components/ReportForms";
import { validationSchemaShows } from "./validation/validation";

export function LicenseReportPageShows() {
  const { res } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [firstLoading, setFirstLoading] = useState<boolean>(true);
  const [loadingBriefInfo, setLoadingBriefInfo] = useState<boolean>(true);
  const [licenseReport, setLicenseReport] = useState<
    ShowsReportInfo | undefined
  >();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loadReportData = useCallback(
    (formikHelper?: FormikHelpers<SubmitShowsReport>) => {
      if (isSubmitting) return;
      const urlParams = LicenseesUtils.getUrlParams();
      if (!urlParams.licenseId || !urlParams.licenseInvoicePlanId) return;

      setLoadingBriefInfo(true);
      api
        .licenseesGetShowsEventReport(
          urlParams.licenseId,
          urlParams.licenseInvoicePlanId,
        )
        .then((res) => {
          const dataNormalized = nullsToUndefined<ShowsReportInfo>(res.data);
          setLicenseReport(dataNormalized);
          if (formikHelper) {
            const submitDataObject = { ...dataNormalized, submitReport: false };
            formikHelper.resetForm({
              values: submitDataObject as SubmitShowsReport,
            });
          }
        })
        .catch(setError)
        .finally(() => {
          setLoadingBriefInfo(false);
          setFirstLoading(false);
        });
    },
    [isSubmitting],
  );

  useEffect(() => {
    if (firstLoading) loadReportData();
  }, [firstLoading, loadReportData]);

  const hasNoPortalReport = useMemo(
    () =>
      LicenseesUtils.hasNoPortalReport(
        licenseReport?.portalReportStatusType,
        licenseReport?.eventNotOccuredPortal,
        licenseReport?.rows?.length,
      ),
    [licenseReport],
  );

  const handleSubmit = (
    formikValues: SubmitShowsReport,
    formikHelpers: FormikHelpers<SubmitShowsReport>,
  ) => {
    if (!licenseReport) return;

    setIsSubmitting(true);

    const values = _.cloneDeep(formikValues);
    if (values.eventNotOccuredPortal) {
      //Empty bag rows with possible incomplete data
      values.rows = [];
    } else {
      prepareCollectionForPost(values, "rows");
    }

    api
      .licenseesPostShowsEventReport(
        licenseReport.licenseHeaderId,
        licenseReport.licenseInvoicePlanId,
        values,
      )
      .then(() => {
        ErrorUtils.showSuccessfulMessage(
          res("message_Success"),
          values.submitReport
            ? res("licensees_LicenseReports_notification_Submit")
            : res("licensees_LicenseReports_notification_Save"),
        );

        if (values.eventNotOccuredPortal) {
          //Manually clear bag rows
          values.rows = [];
        }
        if (values.submitReport) setSubmitted(true);
        loadReportData(formikHelpers);
      })
      .catch((err) => ErrorUtils.handleFormValidation(err, formikHelpers))
      .finally(() => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      });
  };

  const renderActionButtons = useCallback(
    (editAllowed: boolean, formikProps: FormikProps<SubmitShowsReport>) => {
      if (!editAllowed || submitted) return;

      return (
        <SaveAndSubmit
          formikProps={formikProps}
          disabled={isSubmitting}
          setIsModalOpen={setIsModalOpen}
        />
      );
    },
    [isSubmitting, submitted],
  );

  const renderForm = useCallback(
    (formikProps: FormikProps<SubmitShowsReport>) => {
      const editAllowed = AvisUtils.isEditablePortalReportStatusTypeInfo(
        licenseReport?.portalReportStatusType,
      );
      if (loadingBriefInfo && firstLoading && !licenseReport)
        return <AvisLoader inline />;

      if (hasNoPortalReport) return <NotPortalReportMessage />;

      return (
        <>
          <AvisConfirmModalShort
            isOpen={isModalOpen}
            content={res("licensees_LicenseReports_notification_ConfirmSubmit")}
            confirm={formikProps.submitForm}
            cancel={() => setIsModalOpen(false)}
            title={res(
              "licensees_LicenseReports_notification_ConfirmSubmitTitle",
            )}
            disableControls={isSubmitting}
          />
          <ReportShowsForm
            bag={formikProps}
            bagField="rows"
            periodFrom={moment(licenseReport?.periodFrom).toDate()}
            periodTo={moment(licenseReport?.periodTo).toDate()}
            disabled={!editAllowed || submitted}
            loading={isSubmitting}
            allowBlankReport={
              licenseReport?.licenseSettingsInfo?.allowBlankReport
            }
            availableCreations={licenseReport?.creations}
          />
          {renderActionButtons(editAllowed, formikProps)}
        </>
      );
    },
    [
      licenseReport,
      loadingBriefInfo,
      firstLoading,
      hasNoPortalReport,
      isModalOpen,
      res,
      isSubmitting,
      submitted,
      renderActionButtons,
    ],
  );

  if (!licenseReport) return <AvisLoader />;

  return (
    <LicenseReportForm
      data={cloneDeep(licenseReport) as EventReportInfo}
      error={error}
      loading={loadingBriefInfo && firstLoading}
      backLink={{
        title: res("label_Back"),
        url: LicenseesUtils.getUrlWithParams(
          LicenseesUrls.licenseeReports,
          {},
          true,
        ),
      }}
    >
      <LicenseReportHeader data={licenseReport} />

      <Formik<SubmitShowsReport>
        initialValues={{
          eventNotOccuredPortal: licenseReport?.eventNotOccuredPortal,
          submitReport: false,
          rows: licenseReport?.rows as SubmitShowsReportRow[],
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchemaShows(
          res,
          licenseReport?.periodFrom
            ? moment(licenseReport.periodFrom).toDate()
            : undefined,
          licenseReport?.periodTo
            ? moment(licenseReport.periodTo).toDate()
            : undefined,
        )}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {renderForm}
      </Formik>
      {isSubmitting && <AvisLoader />}
    </LicenseReportForm>
  );
}
